/**
 * InAppBrowserScreen - Escape hatch para in-app browsers (WhatsApp, Instagram, etc.)
 * Se muestra cuando la app detecta que corre dentro del WebView de una app social,
 * donde la instalación PWA es imposible.
 *
 * @module components/InAppBrowserScreen
 * @version 6.3.0
 *
 * Visual: patterns de BrowserBlockScreen (card, overlay, animación)
 * pero con gradiente amber/orange para señalar "advertencia".
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Copy, Check, Share, ExternalLink } from 'lucide-react';
import { getDeviceInfo, getInAppBrowserName } from '../utils/deviceUtils';

interface InAppBrowserScreenProps {
  /** Callback cuando el usuario toca "Continuar en este navegador" */
  onDismiss: () => void;
}

export const InAppBrowserScreen: React.FC<InAppBrowserScreenProps> = ({ onDismiss }) => {
  const { isIOS, isAndroid } = getDeviceInfo();
  const browserName = getInAppBrowserName();

  // Animación de entrada — mismo patrón que BrowserBlockScreen
  const [isAnimating, setIsAnimating] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setIsAnimating(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Feedback visual del clipboard
  const [copied, setCopied] = useState(false);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  /**
   * Android: abre la URL actual en Chrome via intent:// scheme.
   * Preserva deep links (pathname + search) desde window.location.
   */
  const handleOpenInChrome = () => {
    const { host, pathname, search } = window.location;
    const intentUrl = `intent://${host}${pathname}${search}#Intent;scheme=https;package=com.android.chrome;end`;
    window.location.href = intentUrl;
  };

  /**
   * iOS: dispara el Share sheet nativo via navigator.share().
   * El Share sheet de Safari incluye "Abrí en Safari" como opción.
   */
  const handleShareIOS = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({
        url: window.location.href,
        title: 'Liga Ajupam',
      });
    } catch (err: any) {
      // AbortError = usuario canceló el share sheet, es normal
      if (err.name !== 'AbortError') {
        console.warn('[InAppBrowserScreen] navigator.share error:', err);
      }
    }
  };

  /**
   * Copia la URL actual al clipboard + muestra feedback visual.
   */
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // Fallback para contextos donde clipboard API está bloqueado
      const textarea = document.createElement('textarea');
      textarea.value = window.location.href;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const subtitleText = browserName
    ? `Abriste el enlace desde ${browserName}.`
    : 'Abriste el enlace desde una aplicación externa.';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative transform transition-all duration-300 ${
          isAnimating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Icono — gradiente amber/orange (diferenciador vs BrowserBlockScreen azul) */}
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br from-amber-400 to-orange-500 shadow-orange-500/30">
            <AlertTriangle size={40} className="text-white" />
          </div>
        </div>

        {/* Título */}
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Abrí en el navegador
        </h2>

        {/* Explicación */}
        <p className="text-gray-600 text-center text-sm mb-1">
          {subtitleText}
        </p>
        <p className="text-gray-600 text-center text-sm mb-6">
          Este navegador no permite instalar la aplicación. Necesitás abrirla en Chrome o Safari para poder instalarla y usar todas las funciones.
        </p>

        {/* ----------------------------------------------------------- */}
        {/* ANDROID: botón primario → intent:// a Chrome                 */}
        {/* ----------------------------------------------------------- */}
        {isAndroid && (
          <button
            onClick={handleOpenInChrome}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-4 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 active:scale-[0.98] mb-3"
          >
            <ExternalLink size={20} />
            Abrí en Chrome
          </button>
        )}

        {/* ----------------------------------------------------------- */}
        {/* iOS: botón primario → Share sheet (navigator.share)          */}
        {/* ----------------------------------------------------------- */}
        {isIOS && (
          <>
            <button
              onClick={handleShareIOS}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-4 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 active:scale-[0.98] mb-3"
            >
              <Share size={20} />
              Compartir / Abrí en Safari
            </button>

            {/* Instrucciones manuales si navigator.share no está disponible */}
            {typeof navigator.share === 'undefined' && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-3">
                <p className="font-semibold text-blue-900 mb-2 flex items-center gap-2 text-sm">
                  <Share size={16} />
                  Instrucciones manuales:
                </p>
                <ol className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="bg-blue-200 text-blue-900 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                    <span>Toca el botón <strong>Compartir</strong> <Share size={13} className="inline align-text-bottom" /> en la barra</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="bg-blue-200 text-blue-900 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                    <span>Seleccioná <strong>"Abrí en Safari"</strong></span>
                  </li>
                </ol>
              </div>
            )}
          </>
        )}

        {/* ----------------------------------------------------------- */}
        {/* Fallback clipboard — ambas plataformas                       */}
        {/* ----------------------------------------------------------- */}
        <button
          onClick={handleCopyLink}
          className="w-full border-2 border-gray-200 text-gray-700 font-semibold py-3 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 active:scale-[0.98] mb-4"
        >
          {copied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
          {copied ? '¡Listo! Abrí Chrome o Safari y pegá el enlace' : 'Copiar enlace'}
        </button>

        {/* ----------------------------------------------------------- */}
        {/* Dismiss — permite continuar al flow de registration          */}
        {/* ----------------------------------------------------------- */}
        <div className="text-center">
          <button
            onClick={onDismiss}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors underline underline-offset-2"
          >
            Continuar en este navegador
          </button>
        </div>
      </div>
    </div>
  );
};

export default InAppBrowserScreen;
